/**
 * The widget runs in the page of the client site, so it shares the localStorage of that site. Its keys are
 * prefixed, so they do not collide with the keys of the site, and a value it cannot parse is ignored.
 * localStorage can also be unavailable (blocked site data, some private modes, sandboxed iframes): the values
 * then live in memory until the page is left, so the widget keeps working.
 */
export const STORAGE_PREFIX = 'hd-widget:'

// the values that could not be written to localStorage
const memory = new Map<string, string>()

const getLocalStorage = (): Storage | null => {
  try {
    // reading window.localStorage itself throws when the site data is blocked
    return window.localStorage
  } catch {
    return null
  }
}

const readRaw = (key: string): string | null => {
  let stored: string | null = null
  try {
    stored = getLocalStorage()?.getItem(key) ?? null
  } catch {
    // unavailable, the value may be in memory
  }
  return stored ?? memory.get(key) ?? null
}

// the value stored under `key`, or `fallback` when there is none or it is not valid JSON
export const readStorage = <T>(key: string, fallback: T): T => {
  const raw = readRaw(STORAGE_PREFIX + key)
  if (raw === null) {
    return fallback
  }
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

// stores `value` as JSON under `key` and lets the components reading it know, see useStorageData
export const writeStorage = (key: string, value: unknown) => {
  const prefixedKey = STORAGE_PREFIX + key
  const raw = JSON.stringify(value)
  try {
    const localStorage = getLocalStorage()
    if (!localStorage) {
      throw new Error('localStorage is unavailable')
    }
    localStorage.setItem(prefixedKey, raw)
    memory.delete(prefixedKey)
  } catch {
    memory.set(prefixedKey, raw)
  }
  window.dispatchEvent(new Event('storage'))
}
